import { translate } from "@/i18n";
import { desktopReceiptsMessages } from "@/i18n/messages/desktop-receipts";

const m = desktopReceiptsMessages;

export interface DesktopInteractionSummary {
  /** Absent when the receipt names no action: the step's own label above it
   *  already says what the call did ("Opening Spotify"). */
  action?: string;
  result: string;
  capturedAfter: boolean;
}

export function summarizeDesktopInteractionReceipt(
  receipt: Record<string, unknown> | undefined,
): DesktopInteractionSummary | undefined {
  if (!receipt) return undefined;
  const operation = record(receipt.interaction_receipt) ?? receipt;
  const actionReceipt = record(operation.action);
  const acknowledgement = record(operation.ack);
  const after = record(operation.after);
  const verification = record(receipt.verification);
  const action = text(operation.action) ?? text(actionReceipt?.kind);
  // A call may now carry a run of actions. Summarising it by its last one would
  // show three clicks as one in the user's own history, and a run that stopped
  // partway as a clean single action.
  const actionCount = count(operation.action_count);
  const ranCount = count(operation.ran_count);
  const verificationStatus = text(verification?.status);
  const stateChanged = boolean(verification?.state_changed);
  const outcome = text(receipt.outcome) ?? text(receipt.status);
  const ack = text(acknowledgement?.received);
  const completed = Array.isArray(receipt.completed)
    ? receipt.completed
        .slice(0, 8)
        .filter((step) => record(step)?.status === "completed").length
    : undefined;
  const result = resultLabel({
    stateChanged,
    verificationStatus,
    outcome,
    ack,
  });

  return {
    action:
      completed !== undefined
        ? translate(m, "sequence")
        : action
          ? runLabel(actionLabel(action), actionCount, ranCount, {
              interrupted: boolean(operation.run_interrupted) === true,
              unfinished: boolean(operation.run_unfinished) === true,
            })
          : undefined,
    result:
      completed === undefined
        ? result
        : translate(m, "actionsCompleted", { count: completed, result }),
    capturedAfter: Boolean(
      record(after?.capture) ?? record(receipt.post_interaction_capture),
    ),
  };
}

/** How many of the actions asked for actually ran, when there was more than
 *  one. Silent for a single action, which is almost every interaction.
 *
 *  "1 of 3" on its own reads as "two of them did not happen", and that is not
 *  what a short run means: results are written after the press, so a run that
 *  was stopped or cancelled can leave an action that happened unrecorded. The
 *  wording says recorded, and says why the run was short when the receipt
 *  knows. */
function runLabel(
  label: string,
  actionCount: number | undefined,
  ranCount: number | undefined,
  run?: { interrupted: boolean; unfinished: boolean },
): string {
  if (actionCount === undefined || actionCount <= 1) return label;
  // Only claim the whole run when the count says so. Every other layer of this
  // feature under-claims on missing data, and a missing ran_count rendering as
  // "x3" would tell the user three clicks happened when we do not know that.
  const count = actionCount;
  if (ranCount === actionCount) return translate(m, "runAll", { label, count });
  // No count to render, so say how many were asked for and nothing more. "of 3"
  // on its own reads as a dropped word.
  if (ranCount === undefined)
    return translate(m, "runRequested", { label, count });
  const ran = ranCount;
  // Interrupted before unfinished: a run can be both at once - the user stops
  // one that has not reported back yet - and being stopped is the more specific
  // thing to tell them. `describeRun` orders these two the same way. It does
  // NOT share the whole-run shortcut above: a run where every action was
  // recorded and was then stopped reads "x3" here while the agent is told the
  // user stopped it. Both are true, and the result column says "Stopped".
  if (run?.interrupted)
    return translate(m, "runStopped", { label, ran, count });
  if (run?.unfinished) return translate(m, "runSoFar", { label, ran, count });
  return translate(m, "runRecorded", { label, ran, count });
}

function count(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) && value >= 0
    ? value
    : undefined;
}

type ReceiptKey = keyof (typeof desktopReceiptsMessages)["en"];
const ACTION_KEYS: Record<
  string,
  Exclude<Extract<ReceiptKey, `action${string}`>, "actionsCompleted">
> = {
  AXPress: "actionPressed",
  AXFocus: "actionFocused",
  click: "actionClicked",
  double_click: "actionDoubleClicked",
  drag: "actionDragged",
  scroll: "actionScrolled",
  key: "actionPressedKey",
};
const LIFECYCLE_KEYS: Record<string, Extract<ReceiptKey, `result${string}`>> = {
  queued: "resultQueued",
  running: "resultPlaying",
  waiting_for_user: "resultWaiting",
  cancelled: "resultStopped",
  stopped: "resultStoppedInspect",
  unknown: "resultUnknown",
  failed: "resultFailed",
  rejected: "resultRejected",
};
function actionLabel(action: string): string {
  const key = ACTION_KEYS[action];
  return key ? translate(m, key) : action.split("_").join(" ");
}

function resultLabel(options: {
  stateChanged: boolean | undefined;
  verificationStatus: string | undefined;
  outcome: string | undefined;
  ack: string | undefined;
}): string {
  if (options.stateChanged) return translate(m, "resultChangeVerified");
  if (options.verificationStatus === "observed_unchanged") {
    return translate(m, "resultNoChange");
  }
  const lifecycle = options.outcome && LIFECYCLE_KEYS[options.outcome];
  if (lifecycle) return translate(m, lifecycle);
  if (options.outcome === "completed" || options.ack === "completed") {
    return translate(m, "resultCompleted");
  }
  return translate(m, "resultRecorded");
}

function record(value: unknown): Record<string, unknown> | undefined {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function text(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function boolean(value: unknown): boolean | undefined {
  return typeof value === "boolean" ? value : undefined;
}
